/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Button from '@react/react-spectrum/Button';
import classNames from 'classnames';
import {IntlProvider} from '@quarry/intl';
import * as localeStringsSideNavMenuButton from './__localization__/SideNavMenuButton';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import Rail from '@react/react-spectrum/Icon/Rail';
import React from 'react';
import Tooltip from '@react/react-spectrum/Tooltip';
import {useIntl} from 'react-intl';

let delayedSideNavOpenTimeout;
function cleanupTimeout () {
  clearTimeout(delayedSideNavOpenTimeout);
  delayedSideNavOpenTimeout = null;
}

function SideNavMenuButton (props) {
  const {sideNavOpen, sideNavOpenByHover, toggleSideNav} = props;
  const {formatMessage} = useIntl();

  const delayedSideNavOpen = e => {
    e.stopPropagation();
    // When a window regains focus it re-fires a focus event on the last thing
    // that was focused before it lost focus. This focus event should be ignored
    // and is detectable by checking the native event for relatedTarget.
    if (sideNavOpen || delayedSideNavOpenTimeout || !e.nativeEvent.relatedTarget) {
      return;
    }
    delayedSideNavOpenTimeout = setTimeout(() => {
      toggleSideNav(true, 'hover');
      cleanupTimeout();
    }, 500);
  };

  const sideNavClose = e => {
    e.stopPropagation();
    if (delayedSideNavOpenTimeout) {
      cleanupTimeout();
    }
    if (sideNavOpen && sideNavOpenByHover) {
      toggleSideNav(true, 'mouseleave');
    }
  };

  const onClick = e => {
    e.stopPropagation();
    if (['Enter', 'Space'].includes(e.code) || e.type === 'click') {
      cleanupTimeout();
      toggleSideNav(true, 'menu');
    }
  };

  function getLockTooltip() {
    return formatMessage({
      defaultMessage: 'Click to lock menu',
      description: 'Tool tip label for locking side nav in open state with hamburger menu button',
      id: 'sidenavMenuLockTooltip'
    });
  }

  function getCloseTooltip() {
    return formatMessage({
      defaultMessage: 'Close menu',
      description: 'Tool tip label for closing side nav with hamburger menu button',
      id: 'sidenavMenuCloseTooltip'
    });
  }

  return (<OverlayTrigger delayShow={1200}>
    <Button
      aria-label={formatMessage({
        defaultMessage: 'Toggle side navigation menu',
        description: 'Label for hamburger button',
        id: 'toggleSideNav'})}
      aria-pressed={sideNavOpen}
      className={classNames('spectrum-Shell-hamburgerMenu-wrapper', {sideNavOpen})}
      id="shell-left-nav-menu-toggle-button"
      onBlur={sideNavClose}
      onClick={onClick}
      onFocus={delayedSideNavOpen}
      onKeyDown={onClick}
      onMouseEnter={delayedSideNavOpen}
      onMouseLeave={sideNavClose}>
      <Rail className="spectrum-Shell-hamburgerMenu" size="S" />
    </Button>
    <Tooltip className={sideNavOpen ? '' : 'spectrum-Shell-hamburgerMenu-tooltip-hide'} placement="right">
      {sideNavOpenByHover && getLockTooltip()}
      {!sideNavOpenByHover && getCloseTooltip()}
    </Tooltip>)
  </OverlayTrigger>);
}

export default function I18nSideNavMenuButton(props) {
  return (
    <IntlProvider messages={localeStringsSideNavMenuButton}>
      <SideNavMenuButton {...props} />
    </IntlProvider>
  );
}
